/**
 * Auth Initializer Component
 * Validates authentication state on app startup and redirects when needed
 */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { clearAuth, verifyTokenAsync } from '../../store/slices/auth.slice';
import { PATHS } from '../../routes/constants/paths';

interface AuthInitializerProps {
  children: React.ReactNode;
}

export const AuthInitializer = ({ children }: AuthInitializerProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);
  const token = useAppSelector(state => state.auth.token);
  const hasVerifiedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!token) return;

    const currentPath = location.pathname;
    if (currentPath === PATHS.RESET_PASSWORD || currentPath === PATHS.VERIFY_EMAIL) return;

    try {
      const parts = token.split('.');
      if (parts.length !== 3) return;

      const base64url = parts[1];
      const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
      const paddedBase64 = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
      const payload = JSON.parse(atob(paddedBase64));

      // Expired token — clear auth and redirect to login
      const now = Math.floor(Date.now() / 1000);
      if (payload?.exp && payload.exp < now) {
        dispatch(clearAuth());
        localStorage.clear();
        navigate(PATHS.LOGIN, { replace: true });
        return;
      }

      // Unconfirmed account — redirect to email verification
      const accountStatus = String(payload?.accountStatus || '').toUpperCase();
      if (accountStatus === 'UNCONFIRMED') {
        navigate(PATHS.VERIFY_EMAIL, { replace: true });
        return;
      }

      // Must reset password — redirect to reset password
      const mustReset =
        Boolean(payload?.credentials?.mustResetPassword) ||
        ['FORCE_CHANGE_PASSWORD', 'RESET_REQUIRED'].includes(accountStatus);
      if (mustReset) {
        navigate(PATHS.RESET_PASSWORD, { replace: true });
      }
    } catch {
      // If token parsing fails, don't block navigation
    }
  }, [isAuthenticated, token, location.pathname, navigate, dispatch]);

  // Refresh auth + agency data on app startup.
  // This avoids stale redux-persist state when approvals happen elsewhere (e.g., Super Admin).
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!token) return;
    if (hasVerifiedRef.current) return;

    hasVerifiedRef.current = true;

    const run = async () => {
      try {
        await dispatch(verifyTokenAsync()).unwrap();
      } catch (error) {
        // If verification fails, don't hard-stop the app here.
        // Interceptors/guards will handle 401s; we just avoid keeping stale state.
        console.error('Failed to refresh session on startup:', error);
      }
    };

    void run();
  }, [dispatch, isAuthenticated, token]);

  return <>{children}</>;
};

export default AuthInitializer;
